import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

/**
 * CinematicHero — slideshow magazine-style alla Tower Eden.
 *
 * Pattern: Ken Burns con DIREZIONI ALTERNATE (più "vivo" di un solo zoom)
 * + cross-fade lento + testi con entrata animata (rise-in + letter-spacing).
 *
 * Solo CSS, niente librerie. Funziona ovunque, anche autoplay mobile.
 */
export default function CinematicHero({
  photos = [],
  title,
  location,
  agencyLogoUrl,
  agencyName,
  height = "h-[68vh] sm:h-[72vh] md:h-[78vh]",
  testid = "cinematic-hero",
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    photos.forEach((src) => { const img = new Image(); img.src = src; });
    const t = setTimeout(() => setReady(true), 50);
    return () => clearTimeout(t);
  }, [photos]);

  if (!photos.length) return null;

  const PER_SLIDE = 6.0;
  const FADE = 1.5;
  const N = photos.length;
  const totalCycle = N * (PER_SLIDE - FADE) + FADE;

  // 6 direzioni FORTI di Ken Burns — zoom 1.0 → 1.30 + pan deciso
  // (Tower Eden style — movimento percepibile a colpo d'occhio)
  const DIRECTIONS = [
    { origin: "20% 20%", endTransform: "scale(1.30) translate(-4%, -3%)" },     // forte zoom angolo top-left + pan
    { origin: "80% 25%", endTransform: "scale(1.28) translate(4%, -3%)" },      // forte zoom angolo top-right + pan
    { origin: "50% 50%", endTransform: "scale(1.25) translate(0, 0)" },         // forte zoom centrale
    { origin: "20% 80%", endTransform: "scale(1.30) translate(-4%, 3%)" },      // forte zoom angolo bottom-left + pan
    { origin: "80% 80%", endTransform: "scale(1.28) translate(4%, 3%)" },       // forte zoom angolo bottom-right + pan
    { origin: "50% 30%", endTransform: "scale(1.32) translate(0, -4%)" },       // forte zoom verso alto
  ];

  return (
    <div
      className={`relative w-full overflow-hidden bg-[#0E0E0C] ${height}`}
      data-testid={testid}
    >
      {/* Slides */}
      {photos.map((src, i) => {
        const d = DIRECTIONS[i % DIRECTIONS.length];
        return (
          <div
            key={i}
            className="absolute inset-0 cinematic-slide"
            style={{
              backgroundImage: `url(${src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              transformOrigin: d.origin,
              "--ken-end": d.endTransform,
              animationDelay: `${i * (PER_SLIDE - FADE)}s`,
              animationDuration: `${totalCycle}s`,
              opacity: ready ? undefined : 0,
            }}
          />
        );
      })}

      {/* Vignette gradient — bottom heavy per leggibilità testo bottom-left */}
      <div
        className="absolute inset-0 pointer-events-none z-[5]"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.0) 35%, rgba(0,0,0,0.0) 55%, rgba(0,0,0,0.75) 100%)",
        }}
      />

      {/* Testo bottom-left magazine-style */}
      <div className="absolute left-0 right-0 bottom-0 z-10 px-6 sm:px-10 md:px-14 pb-8 sm:pb-12 flex items-end justify-between gap-4">
        <div className="min-w-0 flex-1 cinematic-text-block">
          {location && (
            <div
              className="cinematic-text-eyebrow text-[11px] sm:text-[12px] uppercase text-white/95 font-bold mb-3 sm:mb-4"
              style={{ textShadow: "0 2px 12px rgba(0,0,0,0.55)", letterSpacing: "0.32em" }}
            >
              {location}
            </div>
          )}
          {title && (
            <h1
              className="cinematic-text-title font-editorial text-white tracking-tight leading-[1.05] text-[34px] sm:text-[48px] md:text-[60px] lg:text-[72px]"
              style={{ textShadow: "0 2px 24px rgba(0,0,0,0.55)" }}
            >
              {title}
            </h1>
          )}
        </div>

        {/* Agency mark — bottom right */}
        {(agencyLogoUrl || agencyName) && (
          <div className="shrink-0 max-w-[34%] sm:max-w-[28%] cinematic-text-agency self-end" data-testid="cinematic-agency-mark">
            {agencyLogoUrl ? (
              <img
                src={agencyLogoUrl}
                alt={agencyName || "Agenzia"}
                crossOrigin="anonymous"
                className="max-h-12 sm:max-h-16 max-w-full object-contain bg-white/90 backdrop-blur-sm px-3 py-2 rounded-md shadow-lg"
                style={{ filter: "brightness(0)" }}
              />
            ) : (
              <div className="text-right">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-white/75 font-bold mb-0.5" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.6)" }}>
                  Annuncio gestito da
                </div>
                <div className="font-editorial text-white text-[15px] sm:text-[18px] tracking-tight" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.6)" }}>
                  {agencyName}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Slide progress dots */}
      <div className="absolute top-5 sm:top-6 left-0 right-0 z-10 flex items-center justify-center gap-1.5 px-6">
        {photos.map((_, i) => (
          <span
            key={i}
            className="h-[2px] flex-1 max-w-12 bg-white/25 rounded-full overflow-hidden relative"
          >
            <span
              className="absolute inset-y-0 left-0 bg-white/85 cinematic-progress"
              style={{
                animationDelay: `${i * (PER_SLIDE - FADE)}s`,
                animationDuration: `${totalCycle}s`,
              }}
            />
          </span>
        ))}
      </div>

      <style>{`
        /* Ken Burns con direzione variabile via --ken-end */
        @keyframes cinematic-slide-anim {
          0%   { opacity: 0; transform: scale(1.0); }
          ${(FADE / totalCycle * 100).toFixed(2)}%  { opacity: 1; }
          ${((PER_SLIDE - FADE) / totalCycle * 100).toFixed(2)}%  { opacity: 1; }
          ${(PER_SLIDE / totalCycle * 100).toFixed(2)}%  { opacity: 0; transform: var(--ken-end); }
          100% { opacity: 0; transform: var(--ken-end); }
        }
        .cinematic-slide {
          animation-name: cinematic-slide-anim;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
          animation-fill-mode: both;
          will-change: opacity, transform;
        }

        /* Testo eyebrow — fade in + letter-spacing widen */
        @keyframes cinematic-eyebrow-in {
          0%   { opacity: 0; letter-spacing: 0.08em; transform: translateY(8px); }
          70%  { opacity: 1; letter-spacing: 0.32em; transform: translateY(0); }
          100% { opacity: 1; letter-spacing: 0.32em; transform: translateY(0); }
        }
        .cinematic-text-eyebrow {
          animation: cinematic-eyebrow-in 1.6s 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
          letter-spacing: 0.32em;
        }
        @keyframes cinematic-line-grow {
          0%   { width: 0; opacity: 0; }
          100% { width: 28px; opacity: 0.6; }
        }
        .cinematic-line {
          animation: cinematic-line-grow 1.6s 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        /* Titolo — rise in + scale subtle */
        @keyframes cinematic-title-in {
          0%   { opacity: 0; transform: translateY(20px) scale(0.97); filter: blur(4px); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        .cinematic-text-title {
          animation: cinematic-title-in 1.8s 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        /* Agency mark fade in late */
        @keyframes cinematic-agency-in {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .cinematic-text-agency {
          animation: cinematic-agency-in 1.2s 1.5s cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        /* Progress dots */
        @keyframes cinematic-progress-anim {
          0%   { transform: scaleX(0); transform-origin: left; }
          ${(FADE / totalCycle * 100).toFixed(2)}%  { transform: scaleX(0); transform-origin: left; }
          ${(PER_SLIDE / totalCycle * 100).toFixed(2)}%  { transform: scaleX(1); transform-origin: left; }
          100% { transform: scaleX(1); transform-origin: left; }
        }
        .cinematic-progress {
          width: 100%;
          animation-name: cinematic-progress-anim;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
          animation-fill-mode: both;
        }

        @media (prefers-reduced-motion: reduce) {
          .cinematic-slide, .cinematic-progress,
          .cinematic-text-eyebrow, .cinematic-text-title,
          .cinematic-text-agency, .cinematic-line { animation: none; }
        }
      `}</style>
    </div>
  );
}

CinematicHero.propTypes = {
  photos: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string,
  location: PropTypes.string,
  agencyLogoUrl: PropTypes.string,
  agencyName: PropTypes.string,
  height: PropTypes.string,
  testid: PropTypes.string,
};
